import React, { useState, useEffect, Fragment } from 'react';
import { useIntl } from 'react-intl';
import { number, bool, func, shape, arrayOf, string } from 'prop-types';
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import { withStyles, Tooltip } from '@material-ui/core';
import WarningIcon from "@material-ui/icons/Warning";
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import { cloneDeep, difference, findIndex, each, keys, uniq, map } from 'lodash';

import {
  fetchAllTags,
} from '../../../../../actions/api';
import {
  getAllPortalTags,
} from '../../../../../reducers/api';
import {
  fetchProductsApis,
  updateProductApisAccess,
  resetProductApiUpdateFlag,
} from '../../../../../actions/application';
import {
  getProductApisListTotalPages,
  getProductApisListTotalElements,
  getProductApisListResults,
  getIsProductApiUpdateSuccess,
  getIsProductApiError,
  getProductApiErrors,
} from '../../../../../reducers/application';
import styles from './styles';
import ListContainer from '../../../../list';
import {
  GRID_ROWS_PER_PAGE_OPTIONS,
  GRID_ROWS_PER_PAGE_DEFAULT_OPTION,
  ASSIGNED,
  NOT_ASSIGNED,
  ALERT_SUCCESS,
 } from '../../../../../constants';
 import {
  Switch,
  TagsGroup,
  FormActionButtons,
  ToolTip as ToolTipComp,
} from '../../../../../components';
import { getStatusColor, getStatusLabel } from "../../../../Api/List/utils";
import { FilterByApiName, FilterByStates, FilterByTags, FilterAndSortSeparator } from './controls';
import { getI18n, getI18nFormattedMessage } from '../../../../../utils/intl';

export const ApiListRaw = (props) => {
  const {
    productUuid,
    appUuid,
    classes,
    isLoading,
    totalPages,
    totalElements = 0,
    rows = [],
    allTags,
    visible,
    onCancelApiDialog,
    isProductApiUpdateSuccess,
    pageErrors,
    isError,
    hasUpdateProductApis,
  } = props;

  const intl = getI18n(useIntl());
  const [filterByName, setFilterByName] = useState('');
  const [filterByState, setFilterByState] = useState('All');
  const [filterByTag, setFilterByTag] = useState([]);
  const [filterByNameApplied, setFilterByNameApplied] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(GRID_ROWS_PER_PAGE_DEFAULT_OPTION);
  const [currentApisRows, setCurrentApisRows] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [modifiedRecords, setModifiedRecords] = useState(0);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationStatus, setNotificationStatus] = useState('');
  const [errors, setErrors] = useState([]);

  const APIS_LIST_COLUMNS = [{
    id: 'name',
    label: getI18nFormattedMessage('label.api.name'),
    minWidth: 100,
    link: '/publish/apis/details',
  }, {
    id: 'tags',
    label: getI18nFormattedMessage('label.application.details.apis.tags.column.title'),
    minWidth: 300,
    value: (item) => (
      <div className={classes.tagsGroupContainer}>
        <TagsGroup
          onTagClick={(tag) => item.onTagClick(tag, item.tags)}
          tags={item.tags}
        />
      </div>
    ),
  },{
    id: 'portalStatus',
    label: getI18nFormattedMessage('label.state'),
    minWidth: 50,
    value: (item) => {
      const { portalStatus } = item;
      const status = getStatusLabel(portalStatus);
      const statusIconStyle = {
        fill: getStatusColor(portalStatus),
      };
      return (
        <span>
            <FiberManualRecordIcon
              size="0.82em" key={item.apiUuid} style={statusIconStyle} className={classes.statusIcon} />
            {status === 'Disabled' ?
              <span> {status}
                <Tooltip
                  title={getI18nFormattedMessage('label.application.details.disabled.api.locked')}
                  arrow
                  placement="top"
                >
                  <WarningIcon className={classes.warningIcon} />
                </Tooltip>
              </span>
            :
            <span>{status}</span>
            }
        </span>);
    },
  },{
    id: 'appProductApiAccessStatus',
    label: (
      <ToolTipComp
        classes={classes}
        label={getI18nFormattedMessage('label.assign')}
        info={<div>{getI18nFormattedMessage('label.application.details.product.apis.assigned.tooltip')}</div>}
      />
    ),
    minWidth: 50,
    // eslint-disable-next-line react/prop-types
    format: (item) =>
      (<Switch
        id={`${item.apiUuid}-access`}
        onChange={(val) => onAccessToggle(item.apiUuid, val)}
        checked={item.appProductApiAccessStatus === 'ASSIGNED'}
        disabled={item.portalStatus !== 'DEPRECATED' || !hasUpdateProductApis}
      />
    ),
    value: (item) => (item),
  }];

  const onAccessToggle = (uuid, access) => {
    const index = findIndex(currentApisRows, item => item.apiUuid === uuid);
    currentApisRows[index].appProductApiAccessStatus = access ? ASSIGNED : NOT_ASSIGNED;
    setCurrentApisRows([...currentApisRows]);
  };

  const emptyFunction = () => {};

  const notifyMessages = (status, message) => {
    setNotificationStatus(status);
    setNotificationMessage(message);
  };
  
  useEffect(() => {
    setErrors(isError ? pageErrors : []);
  }, [isError, pageErrors]);

  useEffect(() => {
    if (productUuid && visible) {
      setSelectedRows([]);
      fetchListWithFilters();
      props.fetchAllTags();
    }
  }, []);

  useEffect(() => {
    if(isProductApiUpdateSuccess) {
      fetchListWithFilters();
      notifyMessages(ALERT_SUCCESS,
        intl.getI18nMessage('label.applicatiom.details.product.apis.assinged.success'),
      );
      setTimeout(() => {
        notifyMessages('', '');
      }, 2500);
      props.resetProductApiUpdateFlag();
    }
  }, [isProductApiUpdateSuccess]);

  useEffect(() => {
    const rowsData = map(rows, (item) => ({ uuid: item.apiUuid, ...item, onTagClick }));
    setCurrentApisRows(cloneDeep(rowsData));
  }, [rows]);

  useEffect(() => {
    const rowsMap = {};
    each(rows, item => {
      rowsMap[item.apiUuid] = item;
    });
    const currentApisRowsMap = {};
    each(currentApisRows, item => {
      currentApisRowsMap[item.apiUuid] = item;
    });
    const rowIds = keys(rowsMap);
    const currentApisRowsIds = keys(currentApisRowsMap);
    if ( difference(rowIds, currentApisRowsIds).length !== 0) {
      return ;
    }
    let modifiedRecords = 0;
    each(rowIds, id => {
      if (rowsMap[id].appProductApiAccessStatus !==
        currentApisRowsMap[id].appProductApiAccessStatus) {
          currentApisRowsMap[id].isModify = true;
        modifiedRecords++;
      } else {
        currentApisRowsMap[id].isModify = false;
      }
    });
    setModifiedRecords(modifiedRecords);
  }, [currentApisRows]);

  useEffect(() => {
    if (modifiedRecords > 0) {
      setIsFormDirty(true);
    } else {
      setIsFormDirty(false);
    }
  }, [modifiedRecords]);

  const onFilterByNameChange = (value) => {
    setFilterByName(value);
  };

  const onFilterByNameKeyPress = (e) => {
    if (e.key === 'Enter') {
      setFilterByNameApplied(true);
      fetchListWithFilters({ name: filterByName, pageNum: 0 });
    }
  };

  // tags related code
  const allTagsOptions = allTags.map(({ name }) => ({ value: name, label: name }));
  const onTagsSelectionChange = (items) => {
    onTagsSelect(uniq(map(items, item => item.value)));
  };

  const onTagsSelect = (tags) => {
    setPage(0);
    setFilterByTag(uniq(tags));
    fetchListWithFilters({ tags: tags, pageNum: 0 });
  };
  const onTagClick = (tag, apiTags) => {
    onTagsSelect(uniq([...filterByTag, tag]), apiTags);
  };

  const onFilterByStateChange = (newStateBy) => {
    setFilterByState(newStateBy);
    fetchListWithFilters({ state: newStateBy, pageNum: 0 });
  };

  const onChangePage = (newPage) => {
    if (page === newPage) { return; }
    setPage(newPage);
    fetchListWithFilters({ pageNum: newPage });
  };
  const onChangePreviousPage = () => { onChangePage(page - 1); };
  const onChangeNextPage = () => { onChangePage(page + 1); };

  const onChangeRowsPerPage = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchListWithFilters({ rowsPerPageArg: newRowsPerPage, pageNum: 0 });
  };

  const fetchListWithFilters = ({
    name = filterByName,
    pageNum = page,
    rowsPerPageArg = rowsPerPage,
    tags = filterByTag,
    state = filterByState,
  } = { }) => {
    props.fetchProductsApis({
      productUuid: productUuid,
      appUuid: appUuid,
      filterByName : name,
      page: pageNum,
      rowsPerPage: rowsPerPageArg,
      portalStatus: state === 'All' ? 'ENABLED,DEPRECATED,DISABLED' : state,
      tags,
    });
  };

  const onSave = () => {
    props.resetProductApiUpdateFlag();
    const removedApis =
    currentApisRows.filter(item =>
      item.appProductApiAccessStatus === NOT_ASSIGNED && item.isModify);
    if(removedApis.length > 0 ) {
      const data = map(removedApis,
          item => ({ uuid: item.apiUuid }),
        );
      props.updateProductApisAccess({
        appUuid:appUuid,
        productUuid: productUuid,
        action: 'remove',
        data,
      });
    }
    const addedApis =
    currentApisRows.filter(item => item.appProductApiAccessStatus === ASSIGNED && item.isModify);
    if(addedApis.length > 0 ) {
      const data = map(addedApis,
          item => ({ uuid: item.apiUuid }),
        );
      props.updateProductApisAccess({
        appUuid:appUuid,
        productUuid: productUuid,
        data,
      });
    }
  };

  const noResultsMessage = filterByNameApplied ?
    intl.getI18nMessage('label.api.list.no.results') :
    intl.getI18nMessage('label.no.results');

  return (
    <>
      <ListContainer
        listPageId="app-product-apis-list"
        isLoading={isLoading}
        notificationId="app-product-apis-notifications"
        notificationStatus={notificationStatus}
        setNotificationStatus={setNotificationStatus}
        notificationMessage={notificationMessage}
        setNotificationMessage={setNotificationMessage}
        pageHeaderTitle={''}
        pageHeaderTooltipTitle={''}
        addButtonLabel={''}
        onAddBundle={emptyFunction}
        showListHeader={false}
        pageClass={classes.listContent}
        pageBodyClass={classes.pageBodyClass}
        pageFilterAndSortClass={classes.pageFilterAndSortClass}
        hasPaginationDisabled={isFormDirty}
        filterAndSortContent={(
          <Fragment>
            <FilterByApiName
              fieldContainerClass={classes.fieldContainer}
              name={intl.getI18nMessage('label.filter.by')}
              value={filterByName}
              placeholder={intl.getI18nMessage('label.api.name')}
              handleChange={onFilterByNameChange}
              onKeyPress={onFilterByNameKeyPress}
            />
            <FilterByStates
              fieldContainerClass={classes.fieldContainer}
              name={intl.getI18nMessage('label.filter.by')}
              selectFieldClass={classes.selectField}
              value={filterByState}
              handleChange={onFilterByStateChange}
              hideLabel
            />
            <FilterByTags
              fieldContainerClass={classes.tagsContainer}
              name="tags"
              onChange={onTagsSelectionChange}
              noOptionsMessage={() => intl.getI18nMessage('label.application.api.tag.nooptions.text')}
              optionsData={allTagsOptions}
              placeholder={intl.getI18nMessage('label.application.api.tag.placeholder.text')}
              value={filterByTag}
              hideLabel
            />
            <FilterAndSortSeparator />
          </Fragment>
      )}
        columns={APIS_LIST_COLUMNS}
        rows={currentApisRows}
        setSelectedRows={setSelectedRows}
        selectedRows={selectedRows}
        noResultsMessage={noResultsMessage}
        page={page}
        totalElements={totalElements}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={GRID_ROWS_PER_PAGE_OPTIONS}
        onChangeRowsPerPage={onChangeRowsPerPage}
        onChangePage={onChangePage}
        onChangePreviousPage={onChangePreviousPage}
        onChangeNextPage={onChangeNextPage}
        errors={errors}
      />
      <FormActionButtons
       isContainerFooter={true}
        onNextClick={onSave}
        onCancelClick={onCancelApiDialog}
        nextText={intl.getI18nMessage('label.save.button')}
        cancelText={intl.getI18nMessage('label.cancel.button')}
        id="app-product-apis-tab-add-form-buttons"
        isDisabled={modifiedRecords === 0}
        containerClass={modifiedRecords === 0 ?
          classes.hideFooterContainer: classes.showFooterContainer}
        assistText={
          <span>
            {modifiedRecords}  {getI18nFormattedMessage('label.changed')}
            <WarningIcon className={classes.warningIcon} />
            {getI18nFormattedMessage('label.redepolyment.required')}
          </span>
          }
      />
    </>
  );
};

ApiListRaw.propTypes = {
  classes: shape({}),
  isLoading: bool,
  totalPages: number,
  totalElements: number,
  rows: arrayOf(shape({})),
  fetchAllTags: func,
  productUuid: string,
  appUuid: string,
  allTags: arrayOf(shape({})),
  fetchProductsApis: func,
  visible: bool,
  onCancelApiDialog: func,
  updateProductApisAccess: func,
  resetProductApiUpdateFlag: func,
  isProductApiUpdateSuccess: bool,
  isError: bool,
  pageErrors: arrayOf(shape({})),
  hasUpdateProductApis: bool,
};

const mapStateToProps = (state) => ({
  isLoading: false,
  totalPages: getProductApisListTotalPages(state),
  totalElements: getProductApisListTotalElements(state),
  rows: getProductApisListResults(state),
  allTags: getAllPortalTags(state),
  isProductApiUpdateSuccess: getIsProductApiUpdateSuccess(state),
  isError: getIsProductApiError(state),
  pageErrors: getProductApiErrors(state),
});

const mapDispatchToProps = {
  fetchProductsApis,
  fetchAllTags,
  updateProductApisAccess,
  resetProductApiUpdateFlag,
};
ApiListRaw.displayName = 'Apis';

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(ApiListRaw);
